// Template file-tree helpers (#535). A template's pull/push is a FILE-TREE diff over a
// `{ path: body }` map (not the config-as-code entity diff) — plain readers/writers. Text-only
// for now; binary files inside a slice are a documented follow-up.

import { readdirSync, readFileSync, writeFileSync, mkdirSync, existsSync } from 'fs';
import { dirname, join } from 'path';
import { isSafeTemplatePath } from './types.js';

/** Recursively read a directory tree into a flat `{ relativePath: utf8Body }` map (sorted, deterministic). */
export function readDirToFileMap(rootDir: string): Record<string, string> {
  const out: Record<string, string> = {};
  function walk(dir: string, prefix: string): void {
    const entries = readdirSync(dir, { withFileTypes: true }).sort((a, b) => a.name.localeCompare(b.name));
    for (const entry of entries) {
      const abs = join(dir, entry.name);
      const rel = prefix ? `${prefix}/${entry.name}` : entry.name;
      if (entry.isDirectory()) walk(abs, rel);
      else if (entry.isFile()) out[rel] = readFileSync(abs, 'utf-8');
    }
  }
  if (existsSync(rootDir)) walk(rootDir, '');
  return out;
}

/** Thrown by `writeFileMap` (overwrite off) when one or more target files already exist (#540). */
export class FileCollisionError extends Error {
  constructor(public readonly collisions: string[]) {
    super(`Refusing to overwrite ${collisions.length} existing file${collisions.length === 1 ? '' : 's'}`);
    this.name = 'FileCollisionError';
  }
}

/**
 * Return the (sorted) subset of `files` keys that already exist as files under `targetDir`. Unsafe
 * keys are ignored here — `writeFileMap` rejects them before any write — so this only stats writable
 * paths. Used by both the overwrite guard and the `--dry-run` preview.
 */
export function findFileMapCollisions(targetDir: string, files: Record<string, string>): string[] {
  const collisions: string[] = [];
  for (const rel of Object.keys(files)) {
    if (!isSafeTemplatePath(rel)) continue;
    if (existsSync(join(targetDir, rel))) collisions.push(rel);
  }
  return collisions.sort();
}

/**
 * Write a `{ relativePath: body }` map under `targetDir`, creating parent dirs. Re-validates each
 * path (defense-in-depth — the server already constrained the keys via the same isSafeTemplatePath,
 * but the writer is a path-traversal sink). Returns the written relative paths.
 *
 * Safe by default (#540): refuses to clobber any pre-existing file, throwing `FileCollisionError`
 * *before* writing anything (no partial tree), so the silent-overwrite footgun is closed at the
 * sink and every caller inherits it. Callers that legitimately overwrite (e.g. the idempotent
 * `admin skill install` update) must opt in with `{ overwrite: true }`.
 */
export function writeFileMap(
  targetDir: string,
  files: Record<string, string>,
  opts: { overwrite?: boolean } = {},
): string[] {
  // Validate every key up front so a single unsafe path can't leave a half-written tree.
  for (const rel of Object.keys(files)) {
    if (!isSafeTemplatePath(rel)) {
      throw new Error(`Refusing to write unsafe template path: ${rel}`);
    }
  }
  if (!opts.overwrite) {
    const collisions = findFileMapCollisions(targetDir, files);
    if (collisions.length > 0) throw new FileCollisionError(collisions);
  }
  const written: string[] = [];
  for (const [rel, body] of Object.entries(files)) {
    const abs = join(targetDir, rel);
    mkdirSync(dirname(abs), { recursive: true });
    writeFileSync(abs, body, 'utf-8');
    written.push(rel);
  }
  return written;
}
